import React from "react";
import { FormikProps } from "formik";
import { Entry, EntryAddonProduct } from "../../../model/Entry";
import { Product } from "../../../model/Product";
import { Button, Card, CardContent, CardHeader, Grid, Typography, WithStyles } from "@material-ui/core";
import { ApiBackend } from "../../../providers/apibackend";
import FormTextField from "../../Common/FormTextField";
import { IAddOnOrderItem } from "../../../model/IAddOnOrderItem";
import ManageOrderTransferEnervit from "../../Orders/ManageOrderEditItems/ManageOrderTransferEnervit";
import AddEntryAddonDialog from "./AddEntryAddonDialog";

interface IProps extends FormikProps<Entry> {
    entryProduct: Product;
}

interface IState {
    mounted: boolean;
    expanded: boolean;
    addonProducts: Product[];
    allProducts: Product[];
    entryAddons: (EntryAddonProduct | IAddOnOrderItem)[]
    moveAddon: { orderId: string; orderItemId: string } | null;
    addNew: boolean;
}

class EntryAddonsCard extends React.Component<WithStyles & IProps, IState> {
    state: IState;

    private readonly api: ApiBackend;

    constructor(props: WithStyles & IProps) {
        super(props);

        this.api = new ApiBackend();
        this.state = { mounted: false, expanded: true, addonProducts: undefined, allProducts: undefined, entryAddons: undefined, moveAddon: null, addNew: false };
    }

    async componentDidMount(): Promise<void> {
        await this.initData();
    }

    render(): JSX.Element {
        const { values, classes } = this.props;
        const { mounted, expanded, entryAddons } = this.state;

        if (!mounted) {
            return null;
        }

        const hasAddons = entryAddons && entryAddons.length > 0;

        return (
            <>
                <Card>
                    <CardHeader
                        title="Tillval"
                        action={
                            values.id &&
                            <Button color="secondary" onClick={() => this.setState({ addNew: true })}>
                                Lägg till tillägg
                            </Button>
                        }
                    />
                    {expanded && (
                        <CardContent>
                            {hasAddons ? <>
                                {entryAddons.map((addOn, idx) => {
                                    return this.renderAddon(addOn, idx);
                                })}
                            </> : <Typography variant="body2" color="textSecondary">Inga tillägg</Typography>}
                        </CardContent>
                    )}
                </Card>

                {this.state.addNew && this.props.entryProduct &&
                    <AddEntryAddonDialog
                        entry={values}
                        raceProduct={this.props.entryProduct}
                        onAbort={() => this.setState({ addNew: false })}
                        onSave={() => this.handleAddonAdded()}
                    />
                }

                {this.state.moveAddon &&
                    <ManageOrderTransferEnervit
                        sourceOrderId={this.state.moveAddon.orderId}
                        sourceItemId={this.state.moveAddon.orderItemId}
                        addonTypeLabel="tillägg"
                        onAbort={() => this.setState({ moveAddon: null })}
                        onTransferred={() => this.handleAddonMoved()}
                    />
                }
            </>
        )
    }

    private handleAddonAdded = async (): Promise<void> => {
        this.setState({ addNew: false });
        // Ladda om listan så det nya tillägget syns direkt
        await this.initData();
    };

    private handleAddonMoved = (): void => {
        const { moveAddon, entryAddons } = this.state;
        // Ta bort det flyttade tillägget ur listan direkt (blir borta i DB efter flytten)
        const remaining = entryAddons?.filter(x => (x as IAddOnOrderItem).orderItemId !== moveAddon?.orderItemId);
        this.setState({ moveAddon: null, entryAddons: remaining });
    };

    private renderAddon = (addon: EntryAddonProduct | IAddOnOrderItem, idx: number): JSX.Element => {
        const productMatch = this.state.allProducts?.find(x => x.id === addon.productId);
        const variantMatch = productMatch?.variants?.find(x => x.Id === addon.productVariantId);
        const priceGroupMatch = variantMatch?.priceGroups?.find(x => x.id === addon.productVariantPriceGroupId);

        if (!priceGroupMatch) {
            return null;
        }

        // Flyttbara tillägg är fristående addon-orderrader (IAddOnOrderItem har orderId + orderItemId).
        // EntryAddonProduct-varianten saknar orderItemId och dess orderrad är loppraden - får ingen flytt-knapp.
        // Försäkring är knuten till person och ska inte kunna flyttas.
        const asOrderItem = addon as IAddOnOrderItem;
        const movable = !!asOrderItem.orderId && !!asOrderItem.orderItemId && productMatch.type !== "insurance";

        return (
            <Grid key={idx} item lg={3} md={4} sm={6} xs={12}>
                <FormTextField {...this.props}
                    disabled={true}
                    propName={`addOns[${idx}].value`}
                    label={productMatch.name}
                    valueGetter={() => `${variantMatch.Name} (${priceGroupMatch.Name})`} />
                {movable &&
                    <Button size="small" color="secondary"
                        onClick={() => this.setState({ moveAddon: { orderId: asOrderItem.orderId, orderItemId: asOrderItem.orderItemId } })}>
                        Flytta till annan anmälan
                    </Button>
                }
            </Grid>
        );
    };

    private initData = async (): Promise<void> => {
        const { entryProduct, values } = this.props;

        const work = [
            this.api.listProducts(),
            this.api.getEntryAddonOrderItems(values.id)
        ];

        const [productsResponse, entryAddOns] = await Promise.all(work);

        const products = productsResponse as Product[];

        this.setState({
            mounted: true,
            addonProducts: products.filter(f => entryProduct.addOnProducts?.some(fd => fd === f.id)),
            allProducts: products,
            entryAddons: [...values.addOns, ...entryAddOns as IAddOnOrderItem[]]
        });
    };
}

export default EntryAddonsCard;
